import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  X, ArrowRight, Building2, Megaphone, Users, Check,
} from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { MaskedTitle } from "@/components/common/MaskedTitle";
import { Reveal, RevealGroup, RevealItem } from "@/components/common/Reveal";
import { SectionLabel } from "@/components/common/SectionLabel";
import { EditorialMarquee } from "@/components/common/EditorialMarquee";
import { Counter } from "@/components/common/Counter";
import { CTAButton } from "@/components/common/CTAButton";
import {
  SEO, BRAND, PROBLEMS, AUDIENCES, HOW_SOCIETIES, HOW_ADVERTISERS,
  METRICS, DIFFERENCE, IMAGES,
} from "@/data/site";

const audienceIcons = { societies: Building2, advertisers: Megaphone, residents: Users };

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  return (
    <section ref={ref} className="relative bg-ink text-white overflow-hidden">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-96 w-96 rounded-full bg-teal/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-amber/10 blur-[120px]" />

      <div className="container-px pt-36 pb-20 md:pt-44 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <SectionLabel dark>Residential Digital Advertising</SectionLabel>
          </Reveal>
          <MaskedTitle
            className="mt-7 font-display font-800 tracking-tight text-[13vw] leading-[0.92] sm:text-6xl md:text-7xl lg:text-[5.5rem]"
            lines={["Where Communities", "Meet Brands."]}
            accentIndex={1}
          />
          <motion.p
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8 }}
            className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed"
          >
            {BRAND.subline}
          </motion.p>
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <CTAButton to="/societies" variant="primary" data-testid="hero-cta-society">
              Partner Your Society
            </CTAButton>
            <CTAButton to="/advertisers" variant="ghostLight" data-testid="hero-cta-advertise">
              Advertise With Us
            </CTAButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 font-mono text-xs uppercase tracking-[0.15em] text-white/45"
          >
            <span>32″ Portrait Screens</span>
            <span>06:00 - 23:30 Daily</span>
            <span>Per-Impression Billing</span>
          </motion.div>
        </div>

        {/* Clipped-frame photography with parallax */}
        <div className="lg:col-span-5">
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40, clipPath: "inset(12% 0 12% 0)" }}
            animate={{ opacity: 1, y: 0, clipPath: "inset(0% 0 0% 0)" }}
            transition={{ delay: 0.4, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-xl border border-white/10 aspect-[4/5]">
              <motion.img
                src={IMAGES.heroLobby}
                alt="AdX digital screen running a live ad in a residential elevator lobby as residents walk past"
                style={{ y: imgY, scale: imgScale, objectPosition: "center" }}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
              {/* floating spec chip */}
              <div className="absolute left-4 bottom-4 glass-nav-dark rounded-lg border border-white/15 px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal">5-min master cycle</p>
                <p className="mt-1 text-sm font-display font-600">285s paid · 15s community</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Problem = () => (
  <section className="bg-paper py-14 md:py-20">
    <div className="container-px">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <Reveal><SectionLabel>The Problem</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display font-800 text-3xl md:text-4xl tracking-tight text-balance">
              The old model is broken.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="font-display font-700 text-lg md:text-xl shrink-0">
            AdX fixes all three. <span className="text-teal">→</span>
          </p>
        </Reveal>
      </div>
      <RevealGroup className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {PROBLEMS.map((p, i) => (
          <RevealItem key={i} className="flex gap-4 bg-white rounded-xl border border-ink/10 p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-ink/25">
            <span className="grid place-items-center h-9 w-9 shrink-0 rounded-full bg-teal/10">
              <X className="h-5 w-5 text-teal" strokeWidth={2.2} />
            </span>
            <div>
              <h3 className="font-display font-700 text-base leading-snug">{p.title}</h3>
              <p className="mt-2 text-sm text-ink/55 leading-relaxed">{p.detail}</p>
            </div>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

const Solution = () => (
  <section className="bg-white py-24 md:py-32 border-y border-ink/10">
    <div className="container-px">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <Reveal><SectionLabel>The Solution</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight text-balance">
              A fairer deal for everyone.
            </h2>
          </Reveal>
        </div>
      </div>

      <RevealGroup className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10 rounded-xl overflow-hidden">
        {AUDIENCES.map((a) => {
          const Icon = audienceIcons[a.key];
          return (
            <RevealItem key={a.key} className="bg-paper p-8 md:p-10 flex flex-col">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-lg bg-ink text-teal">
                  <Icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink/50">{a.label}</span>
              </div>
              <h3 className="mt-7 font-display font-700 text-2xl">{a.headline}</h3>
              <ul className="mt-6 space-y-3 flex-1">
                {a.points.map((pt, i) => (
                  <li key={i} className="flex gap-3 text-ink/70">
                    <Check className="h-5 w-5 text-teal shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
              <a href={a.to} className="mt-8 inline-flex items-center gap-2 font-display font-600 text-ink hover:text-teal transition-colors">
                Learn more <ArrowRight className="h-4 w-4" />
              </a>
            </RevealItem>
          );
        })}
      </RevealGroup>
    </div>
  </section>
);

const HowColumn = ({ label, steps, accent }) => (
  <div>
    <div className="flex items-center gap-3 mb-8">
      <span className={`h-2 w-2 rounded-full ${accent}`} />
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink/50">{label}</span>
    </div>
    <RevealGroup className="space-y-8">
      {steps.map((s) => (
        <RevealItem key={s.n} className="flex gap-5">
          <span className="font-display font-800 text-2xl text-ink/20 tabular-nums">{s.n}</span>
          <div>
            <h4 className="font-display font-700 text-lg">{s.title}</h4>
            <p className="mt-2 text-ink/60 leading-relaxed">{s.body}</p>
          </div>
        </RevealItem>
      ))}
    </RevealGroup>
  </div>
);

const HowItWorks = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <Reveal><SectionLabel>How It Works</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Two audiences. One transparent platform.
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
        <HowColumn label="For Societies" steps={HOW_SOCIETIES} accent="bg-teal" />
        <HowColumn label="For Advertisers" steps={HOW_ADVERTISERS} accent="bg-amber" />
      </div>
    </div>
  </section>
);

const ByTheNumbers = () => (
  <section className="relative bg-ink text-white py-24 md:py-32 overflow-hidden">
    <img src={IMAGES.networkCity} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-[0.08]" />
    <div className="relative container-px">
      <Reveal><SectionLabel dark>By The Numbers</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          A network built in the open.
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
        {METRICS.map((m) => (
          <div key={m.label} className="border-r border-b border-white/10 p-6 md:p-8">
            <Counter value={m.value} className="font-display font-800 text-5xl md:text-6xl text-teal tabular-nums" />
            <p className="mt-4 font-display font-600">{m.label}</p>
            <p className="mt-1 text-sm text-white/45">{m.note}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const DifferenceStrip = () => (
  <section className="bg-white py-24 md:py-32 border-b border-ink/10">
    <div className="container-px">
      <Reveal><SectionLabel>The AdX Difference</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          They built scale on lock-in.<br className="hidden md:block" /> We build partnerships on fairness.
        </h2>
      </Reveal>
      <RevealGroup className="mt-16 space-y-px bg-ink/10 border border-ink/10 rounded-xl overflow-hidden">
        {DIFFERENCE.map((d, i) => (
          <RevealItem key={i} className="bg-paper grid grid-cols-1 sm:grid-cols-12 items-center gap-2 sm:gap-6 p-6 md:px-10">
            <span className="sm:col-span-3 font-mono text-xs uppercase tracking-[0.18em] text-ink/50">{d.label}</span>
            <span className="sm:col-span-5 font-display font-700 text-xl md:text-2xl flex items-center gap-3">
              <Check className="h-5 w-5 text-teal shrink-0" strokeWidth={2.5} /> {d.adx}
            </span>
            <span className="sm:col-span-4 text-ink/40 line-through decoration-amber/60">{d.old}</span>
          </RevealItem>
        ))}
      </RevealGroup>
      <Reveal delay={0.1}>
        <div className="mt-12">
          <CTAButton to="/societies" variant="outline">See the full comparison</CTAButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const SocialProof = () => (
  <section className="bg-paper py-24 md:py-28">
    <div className="container-px text-center">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-ink/50">A {BRAND.parent} Venture</p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-700 text-2xl md:text-3xl text-ink/70 max-w-3xl mx-auto text-balance">
          Society partner logos and committee testimonials will appear here as the network launches.
        </h2>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
        {[0, 1, 2, 3].map((i) => (
          <RevealItem key={i} className="h-24 rounded-lg border border-dashed border-ink/20 grid place-items-center text-ink/30 font-mono text-xs uppercase tracking-widest">
            [ Logo ]
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo title={SEO.home.title} description={SEO.home.description} />
      <Hero />
      <EditorialMarquee items={["Transparent", "Technology-Led", "Per-Impression", "Community-First", "Self-Serve", "Playback Evidence"]} />
      <Problem />
      <Solution />
      <HowItWorks />
      <ByTheNumbers />
      <DifferenceStrip />
      {/* SocialProof (society partner logos) hidden for now — restore when real logos are available. */}
    </>
  );
}
